/*
* Usage
<a
    data-collapse-control
    data-target="#collapsable-element-selector"
    data-opts="hide-on-open|once|close-on-external-click|open-on-focus"
    data-parent=".selector" // groups collapsibles to enable accordian behaviour
> Toggle the thing </a>
<div id="collapsable-element-selector" class="collapse">
  i am hidden until you toggle the thing
</div>
// or
<a class="collapse-control" href="#collapsable-element-selector">
    Toggle the thing
</a>
// and optionally
<a
    data-collapse-closer
    data-parent=".selector" // enables closing other elements that have the same parent selector
> Another thing </a>
*/
// todo: check multiple targets
// todo: check multiple controls, same target.
const showClass = 'collapsible-show';
const collapseClass = 'collapse';
const controlOpenClass = 'collapse-control--open';

class Collapsible {
  constructor(element) {
    this.controlElement = element;
    this.targetSelector = this.getTargetSelector();
    this.targets = this.getTargets();
    this.opts = this.parseOpts();
    this.parentEl = this.setupParentGroup();
    this.bindEvents();
    this.state = {
      targetsOpen: false,
      justFocused: false,
      executionCount: 0,
    };
    this.initAccessibility();
  }

  initAccessibility = () => {
    if (this.controlElement.nodeName) {
      this.controlElement.setAttribute('role', 'button');
    }
    this.controlElement.setAttribute('aria-expanded', this.state.targetsOpen);
    if (this.targetSelector.startsWith('#')) { // id selector
      this.controlElement.setAttribute('tabindex', '0');
      this.controlElement.setAttribute('aria-controls', this.targetSelector.slice(1));
    }
  };

  get disabled() {
    if (this.opts.runOnce && this.state.executionCount > 0) {
      return true;
    }
    return false;
  }

  parseOpts = () => {
    const dataOpts = this.controlElement.getAttribute('data-opts') || '';
    const opts = dataOpts.split('|').map(s => s.trim());
    return {
      openByDefault: opts.includes('open-by-default'),
      hideOnOpen: opts.includes('hide-on-open'),
      runOnce: opts.includes('once'),
      closeOnExternalClick: opts.includes('close-on-external-click'),
      openOnFocus: opts.includes('open-on-focus'),
    };
  };
  setupParentGroup = () => {
    const parentSelector = this.controlElement.getAttribute('data-parent');
    if (!parentSelector) {
      return null;
    }
    const parentEl = document.querySelector(parentSelector);
    if (!parentEl) {
      return null;
    }
    this.openEvent = new CustomEvent('collapsible:open', {
      detail: {
        collapsibleParent: parentSelector,
        collapsibleTarget: this.targetSelector,
      },
    });
    parentEl.addEventListener('collapsible:open', ({detail}) => {
      if (detail.collapsibleTarget !== this.targetSelector) {
        this.collapseTargets();
      }
    });
    return parentEl;
  };
  emitOpenEvent = () => {
    if (this.parentEl && this.openEvent) {
      this.parentEl.dispatchEvent(this.openEvent);
    }
  };
  getTargetSelector = () => {
    const target = this.controlElement.getAttribute('data-target');
    const href = this.controlElement.getAttribute('href');
    return target || href;
  };
  log = (...args) => {
    if (__DEVELOPMENT) {
      console.log(...args);
    }
  };
  getTargets = () => {
    if (!this.targetSelector) {
      return null;
    }
    const targets = [...document.querySelectorAll(this.targetSelector)];
    if (targets.length === 0) {
      this.log(`There are no collapse targets for this selector ${this.targetSelector}`);
      return null;
    }
    return targets;
  };
  toggle = (open) => {
    this.targets.forEach(targetEl => targetEl.classList[open ? 'add' : 'remove'](showClass));
    this.controlElement.setAttribute('aria-expanded', open);
    this.controlElement.classList[open ? 'add' : 'remove'](controlOpenClass);
    this.state.targetsOpen = open;
  };
  collapseTargets = () => {
    this.toggle(false);
  };
  collapseTrigger = () => {
    this.controlElement.classList.add(collapseClass);
  };
  openTargets = () => {
    if (this.disabled) {
      return;
    }
    this.toggle(true);
    this.state.executionCount += 1;
    if (this.opts.runOnce) {
      this.dispose();
    }
    if (this.opts.hideOnOpen) {
      this.collapseTrigger();
      this.dispose();
    }
    this.emitOpenEvent();
  };
  triggerCollapsible = () => {
    if (this.state.targetsOpen) {
      this.collapseTargets();
    } else {
      this.openTargets();
    }
  };
  handleClick = (ev) => {
    ev.stopPropagation();
    ev.preventDefault();
    if (this.state.justFocused === true) {
      // focus event can cause item to open if this setting has been enabled
      // this is a fix to prevent the click event closing the focus event
      this.state.justFocused = false;
    } else {
      this.triggerCollapsible();
    }
  };
  handleFocus = () => {
    this.state.justFocused = true;
    this.triggerCollapsible();
    setTimeout(() => this.state.justFocused = false, 1000);
  };
  handleKeyUp = (ev) => {
    // enter, space keys
    if (![13, 32].includes(ev.keyCode)) {
      return;
    }
    ev.preventDefault();
    this.triggerCollapsible();
  };
  handleFocusOut = (ev) => {
    const isFocusLeavingTarget = !this.targets.some(target => target.contains(ev.relatedTarget)) &&
                                 ev.relatedTarget !== this.controlElement;
    if (isFocusLeavingTarget) {
      this.collapseTargets();
    }
  };
  handleExternalEvent = (ev) => {
    if (!this.opts.closeOnExternalClick) {
      return;
    }
    if (!this.state.targetsOpen) {
      return;
    }
    if (ev.type === 'click') {
      if (ev.target === this.controlElement) {
        return;
      }
      if (this.targets.includes(ev.target.closest(`.${showClass}`))) {
        return;
      }
      this.collapseTargets();
    }
    if (ev.type === 'keydown') {
      if (ev.keyCode === 27) {
        this.collapseTargets();
      }
    }
  };
  /*
  * to close collapsibles emit a custom event on the window object of type collapsible:close
  * with an optional event property detail.test which takes the collapsible instance as an
  * argument and returns true to close of false to ignore.
  * */
  handleGlobalCloseEvent = ({detail: {test = () => true}}) => {
    if (test(this)) {
      this.collapseTargets();
    }
  }
  bindEvents = () => {
    this.controlElement.addEventListener('click', this.handleClick);
    this.controlElement.addEventListener('keydown', this.handleKeyUp);
    this.controlElement.addEventListener('focusout', this.handleFocusOut);
    this.targets.forEach(target => target.addEventListener('focusout', this.handleFocusOut));
    window.addEventListener('collapsible:close', this.handleGlobalCloseEvent);
    if (this.opts.openOnFocus) {
      this.controlElement.addEventListener('focus', this.handleFocus);
    }
    if (this.opts.closeOnExternalClick) {
      window.addEventListener('click', this.handleExternalEvent);
      window.addEventListener('keydown', this.handleExternalEvent);
    }
  };
  dispose = () => {
    this.controlElement.removeEventListener('click', this.handleClick);
    this.controlElement.removeEventListener('keydown', this.handleKeyUp);
    this.controlElement.removeEventListener('focusout', this.handleFocusOut);
    this.targets.forEach(target => target.removeEventListener('focusout', this.handleFocusOut));
    if (this.opts.openOnFocus) {
      this.controlElement.removeEventListener('focus', this.handleFocus);
    }
    if (this.opts.closeOnExternalClick) {
      window.removeEventListener('click', this.handleExternalEvent);
    }
  }
}

/* It is sometimes necessary to register controls that collapse collapsibles from elements that are not collapsible
* themselves. The below function handles that. Note: this enables closing of sibling collapsible elements only. */
function registerCollapsibleClosers(collapsibleCloser) {
  collapsibleCloser.addEventListener('focus', () => {
    window.dispatchEvent(new CustomEvent('collapsible:close', {
      detail: {
        test: (collapsible) => {
          const controlParent = document.querySelector(collapsibleCloser.getAttribute('data-parent'));
          return collapsible.parentEl === controlParent;
        }
      }
    }));
  });
}

function setup(collapsibles) {
  collapsibles.forEach(el => new Collapsible(el));
  [...document.querySelectorAll('[data-collapse-closer]')].forEach(el => registerCollapsibleClosers(el));
}

export default setup;
